import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';

// Usar la interfaz UserProfile existente en lugar de crear una nueva
export interface WorkerProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  photoURL?: string;
  sector: 'health' | 'construction';
  role: 'worker' | 'premiumWorker' | 'specialWorker';
  status: 'active' | 'pending';
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    apartment?: string;
    number?: string;
  };
  personalDescription?: string;
  experience: {
    years: number;
    description: string;
    skills: Array<{
      id: string;
      categoryId: string;
      name_en: string;
      name_es: string;
    }>;
  };
  certifications: Array<{
    id?: string;
    name: string;
    name_en?: string;
    name_es?: string;
    issuer: string;
    issuer_en?: string;
    issuer_es?: string;
    date: string;
    expiryDate?: string;
    status: 'approved' | 'rejected' | 'pending';
  }>;
  completedJobs?: number;
  rating?: number;
  reviews?: number;
  identification?: {
    type: string;
    number: string;
  };
}

// Función para generar QR code
const generateQRCode = async (text: string): Promise<string> => {
  try {
    console.log('Generating QR code for text:', text);
    const qrDataUrl = await QRCode.toDataURL(text, {
      width: 80,
      margin: 1,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      },
      errorCorrectionLevel: 'M'
    });
    console.log('QR code generated successfully');
    return qrDataUrl;
  } catch (error) {
    console.error('Error generating QR code:', error);
    return '';
  }
};

// Función para obtener el nombre de la categoría
const getCategoryName = (categoryId: string, profileData: WorkerProfileData, language: 'es' | 'en') => {
  const categories: Record<string, Record<string, { en: string; es: string }>> = {
    construction: {
      cat001: { en: "General Construction", es: "Construcción General" },
      cert001: { en: "General Safety Certifications", es: "Certificaciones Generales de Seguridad" },
      cert002: { en: "Environmental Hazard Certifications", es: "Certificaciones de Riesgos Ambientales" },
      cert003: { en: "Mold Certifications", es: "Certificaciones de Moho" },
      cert004: { en: "Worksite Safety Certifications", es: "Certificaciones de Seguridad en el Sitio" }
    },
    health: {
      cat001: { en: "General Health", es: "Salud General" }
    }
  };

  const sectorCategories = categories[profileData.sector || 'construction'];
  const category = sectorCategories?.[categoryId];
  
  if (category) {
    return language === 'es' ? category.es : category.en;
  }
  
  return categoryId;
};

// Función para generar el encabezado (Logo de Brix)
export const generateHeaderHTML = () => {
  return `
    <div style="text-align: right; margin-bottom: 30px; padding: 10px;">
      <img src="/path/to/logo.png" alt="Brix Logo" style="width: 200px; height: auto;" />
    </div>
  `;
};

// Función para generar el pie de página (adaptativo según idioma)
export const generateFooterHTML = (language: 'es' | 'en') => {
  const footerText = language === 'es' 
    ? 'Creado en Brix, una plataforma de www.sideec.com'
    : 'Created on Brix, a platform of www.sideec.com';
    
  return `
    <div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; width: 100%; box-sizing: border-box;">
      <p style="font-size: 14px; color: #6b7280; margin: 0; font-weight: 500; line-height: 1.6;">
        ${footerText}
      </p>
    </div>
  `;
};

// Función para generar el contenido del usuario
export const generateContentHTML = (profileData: WorkerProfileData, qrCodeDataUrl: string, language: 'es' | 'en') => {
  return `
    <!-- Contenido principal con dos columnas -->
    <div style="display: flex; gap: 40px; margin-bottom: 40px;">
      <!-- Columna izquierda (30%) -->
      <div style="width: 30%; border: 2px solid #dc2626; border-radius: 12px; padding: 20px; display: flex; flex-direction: column; align-items: center; justify-content: flex-start;">
        <!-- Foto de perfil en círculo con borde rojo -->
        <div style="text-align: center; margin-bottom: 20px; width: 100%;">
          <div style="width: 150px; height: 150px; border-radius: 50%; border: 4px solid #dc2626; overflow: hidden; margin: 0 auto 15px; display: flex; align-items: center; justify-content: center; background-color: #f3f4f6;">
            ${profileData.photoURL ? 
              `<img src="${profileData.photoURL}" alt="Profile" style="width: 100%; height: 100%; object-fit: contain;" />` : 
              `<div style="font-size: 48px; color: #9ca3af;">👤</div>`
            }
          </div>
        </div>
        
        <!-- Nombre del usuario -->
        <div style="text-align: center; margin-bottom: 20px; width: 100%;">
          <h2 style="font-size: 20px; font-weight: bold; color: #1f2937; margin: 0;">
            ${profileData.firstName} ${profileData.lastName}
          </h2>
        </div>
        
        <!-- QR Code -->
        <div style="text-align: center; margin-top: 20px; width: 100%;">
          <div style="display: inline-block;">
            <div style="text-align: center;">
              <div style="width: 120px; height: 120px; margin: 0 auto; background-color: white; border-radius: 8px; padding: 8px; display: flex; align-items: center; justify-content: center;">
                ${qrCodeDataUrl ? 
                  `<img src="${qrCodeDataUrl}" alt="QR Code" style="width: 100%; height: 100%; object-fit: contain; min-width: 80px; min-height: 80px;" />` :
                  `<div style="text-align: center;">
                    <span style="font-size: 16px; color: #dc2626; text-align: center; font-weight: bold; display: block;">QR</span>
                    <span style="font-size: 16px; color: #dc2626; text-align: center; font-weight: bold; display: block;">CODE</span>
                    <span style="font-size: 10px; color: #dc2626; text-align: center; display: block; margin-top: 4px;">Not Found</span>
                  </div>`
                }
              </div>
              <p style="font-size: 14px; color: #dc2626; margin: 8px 0 0 0; font-weight: bold;">SCAN QR CODE</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Columna derecha (70%) -->
      <div style="width: 70%; border: 2px solid #dc2626; border-radius: 12px; padding: 20px;">
        <!-- Título de la sección -->
        <h3 style="font-size: 18px; font-weight: bold; color: #dc2626; margin-bottom: 20px; border-bottom: 2px solid #dc2626; padding-bottom: 8px;">
          Personal Information
        </h3>
        
        <!-- Información personal en formato resume -->
        <div style="margin-bottom: 20px;">
          <div style="margin-bottom: 15px;">
            <h4 style="font-size: 16px; font-weight: bold; color: #1f2937; margin: 0 0 8px 0;">${profileData.firstName} ${profileData.lastName}</h4>
            <p style="font-size: 14px; color: #6b7280; margin: 0 0 4px 0;">📧 ${profileData.email}</p>
            <p style="font-size: 14px; color: #6b7280; margin: 0 0 4px 0;">📱 ${profileData.phone || 'N/A'}</p>
            <p style="font-size: 14px; color: #6b7280; margin: 0;">📍 ${profileData.address?.street || 'N/A'}, ${profileData.address?.city || 'N/A'}, ${profileData.address?.state || 'N/A'} ${profileData.address?.zipCode || 'N/A'}, ${profileData.address?.country || 'N/A'}</p>
          </div>
        </div>
        
        <!-- Descripción personal -->
        <div style="margin-bottom: 20px;">
          <label style="font-size: 12px; font-weight: bold; color: #374151; display: block; margin-bottom: 8px;">Personal Description</label>
          <p style="font-size: 14px; color: #1f2937; margin: 0; padding: 12px; background-color: #f9fafb; border-radius: 4px; line-height: 1.5; min-height: 60px;">
            ${profileData.personalDescription || 'No description available'}
          </p>
        </div>
      </div>
    </div>
    
    <!-- Sección de Experiencia -->
    <div style="margin-top: 40px; border: 2px solid #dc2626; border-radius: 12px; padding: 20px;">
      <h3 style="font-size: 18px; font-weight: bold; color: #dc2626; margin-bottom: 20px; border-bottom: 2px solid #dc2626; padding-bottom: 8px;">
        Experience & Skills
      </h3>
      
      ${(() => {
        if (!profileData.experience?.skills || profileData.experience.skills.length === 0) {
          return `<div style="text-align: center; padding: 20px; background-color: #f9fafb; border-radius: 8px;">
            <p style="font-size: 14px; color: #6b7280; margin: 0;">No skills available</p>
          </div>`;
        }

        // Agrupar skills por categoría
        const skillsByCategory = profileData.experience.skills.reduce((acc: any, skill: any) => {
          const categoryId = skill.categoryId || 'general';
          const categoryName = getCategoryName(categoryId, profileData, language);
          
          if (!acc[categoryName]) {
            acc[categoryName] = [];
          }
          acc[categoryName].push(skill);
          return acc;
        }, {});

        // Generar HTML para cada categoría
        return Object.entries(skillsByCategory).map(([categoryName, skills]: [string, any]) => `
          <div style="margin-bottom: 25px;">
            <h4 style="font-size: 16px; font-weight: bold; color: #374151; margin: 0 0 12px 0; padding-bottom: 4px; border-bottom: 1px solid #e5e7eb;">
              ${categoryName}
            </h4>
            <div style="display: flex; flex-wrap: wrap; gap: 8px;">
              ${skills.map((skill: any) => `
                <span style="
                  background-color: #dc2626; 
                  color: white; 
                  padding: 0 16px; 
                  border-radius: 20px; 
                  font-size: 12px; 
                  font-weight: 500; 
                  display: inline-block; 
                  text-align: center; 
                  height: 32px;
                  line-height: 32px;
                  white-space: nowrap;
                  box-sizing: border-box;
                  margin: 0;
                  position: relative;
                ">
                  ${skill.name_en || skill.name_es || skill.name || 'Skill'}
                </span>
              `).join('')}
            </div>
          </div>
        `).join('');
      })()}
    </div>
    

  `;
};

// Función para generar la segunda página con certificaciones
export const generateCertificationsPageHTML = (profileData: WorkerProfileData, language: 'es' | 'en') => {
  return `
    <div style="font-family: Montserrat, Arial, sans-serif; color: #1f2937; max-width: 794px; margin: 0 auto;">
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap');
      </style>
      
      <!-- ENCABEZADO: Logo de Brix -->
      ${generateHeaderHTML()}
      
      <!-- TÍTULO DE LA PÁGINA -->
      <div style="text-align: center; margin-bottom: 40px;">
        <h2 style="font-size: 24px; font-weight: bold; color: #dc2626; margin: 0;">
          ${language === 'es' ? 'Certificaciones' : 'Certifications'}
        </h2>
      </div>
      
      <!-- SECCIÓN DE CERTIFICACIONES -->
      <div style="border: 2px solid #dc2626; border-radius: 12px; padding: 20px;">
        ${(() => {
          if (!profileData.certifications || profileData.certifications.length === 0) {
            return `<div style="text-align: center; padding: 20px; background-color: #f9fafb; border-radius: 8px;">
              <p style="font-size: 14px; color: #6b7280; margin: 0;">
                ${language === 'es' ? 'No hay certificaciones disponibles' : 'No certifications available'}
              </p>
            </div>`;
          }

          return profileData.certifications.map((cert: any) => `
            <div style="margin-bottom: 20px; padding: 15px; background-color: #f9fafb; border-radius: 8px; border-left: 4px solid #dc2626;">
              <h4 style="font-size: 16px; font-weight: bold; color: #1f2937; margin: 0 0 8px 0;">
                ${cert.name_en || cert.name_es || cert.name || 'Certification'}
              </h4>
              <p style="font-size: 14px; color: #6b7280; margin: 0 0 8px 0;">
                <strong>${language === 'es' ? 'Emisor:' : 'Issuer:'}</strong> ${cert.issuer_en || cert.issuer_es || cert.issuer || 'N/A'}
              </p>
              <div style="display: flex; gap: 20px; font-size: 12px; color: #6b7280;">
                <span><strong>${language === 'es' ? 'Fecha:' : 'Date:'}</strong> ${cert.date || 'N/A'}</span>
                ${cert.expiryDate ? `<span><strong>${language === 'es' ? 'Vence:' : 'Expires:'}</strong> ${cert.expiryDate}</span>` : ''}
              </div>
            </div>
          `).join('');
        })()}
      </div>
      
      <!-- PIE DE PÁGINA: Texto adaptativo según idioma -->
      ${generateFooterHTML(language)}
    </div>
  `;
};

export const exportWorkerProfileToPDF = async (
  profileData: WorkerProfileData,
  language: 'es' | 'en' = 'es'
): Promise<void> => {
  try {
    // Usar QR code guardado en Firestore o generar uno nuevo
    let qrCodeDataUrl = (profileData as any).qrCodeUrl;
    
    if (!qrCodeDataUrl) {
      // Generar QR code para el perfil público
      const userId = (profileData as any).id || (profileData as any).uid || (profileData as any).userId || 'profile';
      const publicProfileUrl = `https://brix-v2.web.app/worker/${userId}`;
      console.log('Generating QR for URL:', publicProfileUrl);
      qrCodeDataUrl = await generateQRCode(publicProfileUrl);
      console.log('QR Code generated:', qrCodeDataUrl ? 'Success' : 'Failed');
    }
    
    // Crear el PDF
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const margin = 20; // Margen en mm
    

    
    // Generar la primera página con información del perfil
    const firstPageHTML = `
      <div style="font-family: Montserrat, Arial, sans-serif; color: #1f2937; max-width: 794px; margin: 0 auto;">
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap');
        </style>
        
        <!-- ENCABEZADO: Logo de Brix -->
        ${generateHeaderHTML()}
        
        <!-- CONTENIDO: Información del usuario -->
        ${generateContentHTML(profileData, qrCodeDataUrl, language)}
        
        <!-- PIE DE PÁGINA: Texto adaptativo según idioma -->
        ${generateFooterHTML(language)}
      </div>
    `;
    
    // Generar la segunda página con certificaciones
    const secondPageHTML = generateCertificationsPageHTML(profileData, language);
    
    // Crear contenedores temporales para cada página
    const firstPageContainer = document.createElement('div');
    firstPageContainer.style.position = 'absolute';
    firstPageContainer.style.left = '-9999px';
    firstPageContainer.style.top = '0';
    firstPageContainer.style.width = '794px';
    firstPageContainer.style.backgroundColor = 'white';
    firstPageContainer.style.padding = '40px';
    firstPageContainer.style.fontFamily = 'Montserrat, Arial, sans-serif';
    firstPageContainer.style.lineHeight = '1.5';
    firstPageContainer.style.color = '#1f2937';
    firstPageContainer.style.fontSize = '14px';
    
    const secondPageContainer = document.createElement('div');
    secondPageContainer.style.position = 'absolute';
    secondPageContainer.style.left = '-9999px';
    secondPageContainer.style.top = '0';
    secondPageContainer.style.width = '794px';
    secondPageContainer.style.backgroundColor = 'white';
    secondPageContainer.style.padding = '40px';
    secondPageContainer.style.fontFamily = 'Montserrat, Arial, sans-serif';
    secondPageContainer.style.lineHeight = '1.5';
    secondPageContainer.style.color = '#1f2937';
    secondPageContainer.style.fontSize = '14px';
    
    // Configurar contenido de las páginas
    firstPageContainer.innerHTML = firstPageHTML;
    secondPageContainer.innerHTML = secondPageHTML;
    
    // Agregar contenedores al DOM
    document.body.appendChild(firstPageContainer);
    document.body.appendChild(secondPageContainer);
    
    // Esperar a que el contenido se renderice
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Capturar la primera página
    const firstPageCanvas = await html2canvas(firstPageContainer, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 794,
      scrollX: 0,
      scrollY: 0,
      logging: false
    });
    
    // Capturar la segunda página
    const secondPageCanvas = await html2canvas(secondPageContainer, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 794,
      scrollX: 0,
      scrollY: 0,
      logging: false
    });
    
    // Calcular dimensiones para A4
    const imgWidth = pageWidth - (2 * margin);
    const firstPageImgHeight = (firstPageCanvas.height * imgWidth) / firstPageCanvas.width;
    const secondPageImgHeight = (secondPageCanvas.height * imgWidth) / secondPageCanvas.width;
    
    console.log('First page dimensions:', { imgWidth, firstPageImgHeight, pageHeight, canvasHeight: firstPageCanvas.height, canvasWidth: firstPageCanvas.width });
    console.log('Second page dimensions:', { imgWidth, secondPageImgHeight, pageHeight, canvasHeight: secondPageCanvas.height, canvasWidth: secondPageCanvas.width });
    
    // Agregar primera página
    const firstPageImgData = firstPageCanvas.toDataURL('image/png', 1.0);
    pdf.addImage(firstPageImgData, 'PNG', margin, margin, imgWidth, firstPageImgHeight);
    
    // Agregar segunda página
    pdf.addPage();
    const secondPageImgData = secondPageCanvas.toDataURL('image/png', 1.0);
    pdf.addImage(secondPageImgData, 'PNG', margin, margin, imgWidth, secondPageImgHeight);
    
    // Guardar el PDF
    pdf.save(`worker-profile-${profileData.firstName}-${profileData.lastName}.pdf`);
    
    // Limpiar los contenedores temporales
    document.body.removeChild(firstPageContainer);
    document.body.removeChild(secondPageContainer);
    
    console.log('PDF generated successfully');
    
  } catch (error) {
    console.error('Error exporting PDF:', error);
    throw new Error('Failed to export PDF');
  }
};